import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Copy } from 'lucide-react';

/**
 * JSON 树状节点组件（递归）
 * 用于 JSON 工具 Tab 的树状视图
 */
function JsonTreeNode({ node, onToggle, selectedNodeId, onNodeSelect }) {
  const [showActions, setShowActions] = useState(false);

  // 判断当前节点是否被选中
  const isSelected = selectedNodeId === node.id;

  // 处理节点点击（支持所有类型节点）
  const handleNodeClick = () => {
    if (onNodeSelect) {
      onNodeSelect(node);
    }
  };

  // 复制值
  const handleCopyValue = (e) => {
    e.stopPropagation();
    const value = typeof node.value === 'object'
      ? JSON.stringify(node.value, null, 2)
      : String(node.value);
    navigator.clipboard.writeText(value);
  };

  // 复制路径
  const handleCopyPath = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(node.id);
  };

  // 获取值的颜色（支持深色模式）
  const getValueColor = () => {
    switch (node.type) {
      case 'string':
        return 'text-emerald-600 dark:text-emerald-400';
      case 'number':
        return 'text-blue-500 dark:text-blue-400';
      case 'boolean':
        return 'text-orange-500 dark:text-orange-400';
      case 'null':
        return 'text-gray-400 dark:text-gray-500 italic';
      case 'undefined':
        return 'text-gray-400 dark:text-gray-500 italic';
      case 'object':
        return 'text-purple-600 dark:text-purple-400';
      case 'array':
        return 'text-indigo-600 dark:text-indigo-400';
      default:
        return 'text-gray-800 dark:text-gray-200';
    }
  };

  // 格式化显示值
  const formatDisplayValue = () => {
    if (node.type === 'string') {
      return `"${node.value}"`;
    }
    if (node.type === 'null') {
      return 'null';
    }
    if (node.type === 'object' || node.type === 'array') {
      // 展开时显示开始符号，折叠时显示省略符号
      if (node.isExpanded && hasChildren) {
        return node.type === 'array' ? '[' : '{';
      }
      return node.displayValue; // [...]  或 {...}
    }
    return String(node.value);
  };

  const isExpandable = node.type === 'object' || node.type === 'array';
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="relative">
      {/* 缩进引导线 */}
      {node.level > 0 && (
        <div
          className="absolute top-0 bottom-0 border-l border-gray-200 dark:border-gray-700"
          style={{ left: `${(node.level - 1) * 20 + 16}px` }}
        />
      )}

      {/* 当前节点 */}
      <div
        className={`flex items-center px-2 py-1 rounded transition-all duration-200 group relative ${
          isSelected
            ? 'bg-blue-100 dark:bg-blue-900 hover:bg-blue-200 dark:hover:bg-blue-800 shadow-sm'
            : onNodeSelect
            ? 'hover:bg-blue-50 dark:hover:bg-gray-700 hover:shadow-sm cursor-pointer'
            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}
        style={{ paddingLeft: `${node.level * 20 + 8}px` }}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
        onClick={handleNodeClick}
      >
        {/* 展开/折叠图标 */}
        <div className="flex-shrink-0 w-6 h-6 mr-1 flex items-center justify-center">
          {isExpandable && hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation(); // 阻止冒泡，防止触发节点选中
                onToggle(node.id);
              }}
              className="w-4 h-4 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            >
              {node.isExpanded ? (
                <ChevronDown size={12} className="text-gray-500 dark:text-gray-400" />
              ) : (
                <ChevronRight size={12} className="text-gray-500 dark:text-gray-400" />
              )}
            </button>
          ) : (
            <span className="w-4 h-4 inline-block"></span>
          )}
        </div>

        {/* 键名 */}
        {node.key !== null && node.key !== 'root' && !/^\[\d+\]$/.test(node.key) && (
          <span className="font-mono text-sm mr-1 text-fuchsia-600 dark:text-fuchsia-400">
            {node.key}:
          </span>
        )}

        {/* 值 */}
        <span className={`font-mono text-sm ${getValueColor()} flex-1`}>
          {formatDisplayValue()}
        </span>

        {/* 从字符串解析标记 */}
        {node.isFromStringParse && (
          <span
            className="ml-2 text-xs text-blue-500 dark:text-blue-400 flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded"
            title={`原始字符串: ${node.originalStringPreview || ''}`}
          >
            🔗 <span className="hidden sm:inline">从字符串解析</span>
          </span>
        )}

        {/* 操作按钮（悬浮显示） */}
        {showActions && (
          <div className="flex items-center gap-1 ml-2">
            <button
              onClick={handleCopyValue}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
              title="复制值"
            >
              <Copy size={14} className="text-gray-600 dark:text-gray-300" />
            </button>
            <button
              onClick={handleCopyPath}
              className="px-2 py-1 text-xs hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors text-gray-600 dark:text-gray-300"
              title="复制路径"
            >
              路径
            </button>
          </div>
        )}
      </div>

      {/* 递归渲染子节点 */}
      {node.isExpanded && hasChildren && (
        <div>
          {node.children.map((child, index) => (
            <JsonTreeNode
              key={child.id || index}
              node={child}
              onToggle={onToggle}
              selectedNodeId={selectedNodeId}
              onNodeSelect={onNodeSelect}
            />
          ))}
          {/* 结束符号 */}
          <div
            className="flex items-center px-2"
            style={{ paddingLeft: `${node.level * 20 + 8}px` }}
          >
            <div className="flex-shrink-0 w-6 h-6 mr-1"></div>
            <span className={`font-mono text-sm ${getValueColor()}`}>
              {node.type === 'array' ? ']' : '}'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default JsonTreeNode;
